import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiDocumentationFieldEnum } from 'graphql-common';
import { IconButtonTypes } from '@lib/components/IconButton/IconButton';
import { useSharedAiBudget } from 'components/AiAssistant/AiBudgetContext';
import { AiCredits } from 'components/AiAssistant/AiCreditsIndicator';
import { getAiBudgetBlockedText } from 'components/AiAssistant/utils/getAiBudgetBlock';
import { AiFieldUiState } from 'routes/Documentation/utils/getAiFieldDetectionState';
import { useSharedAiFieldGeneration } from 'routes/Documentation/components/AiDetection/AiFieldGenerationContext';
import useAiFieldGeneration, {
  AiFieldGenerationSource,
} from 'routes/Documentation/DocumentForm/utils/useAiFieldGeneration';
import AiFieldAssistPopover from './AiFieldAssistPopover';
import CodePickerContent from './CodePickerContent';
import DetectingContent from './DetectingContent';
import ErrorContent from './ErrorContent';
import MainContent from './MainContent';

export type AiFieldAssistLabels = {
  // Idle-state label (non-interactive): "Detect with AI" / "Generate with AI" / "Suggest with AI".
  idleLabel: string;
  // Idle-state button text: "Detect" / "Generate" / "Suggest".
  idleCta: string;
  // Post-generation button text: "Detect again" / "Regenerate".
  regenerateCta: string;
};

type Props = {
  // Which documentation field the BE should generate.
  field: AiDocumentationFieldEnum;
  labels: AiFieldAssistLabels;
  // Resolves the generation source (a saved version to regenerate from, or a
  // freshly-uploaded attachment). The form resolves this from react-hook-form
  // state (see AiFormFieldAssist); List/View usage always has an existing
  // document, so it's just `() => ({ documentationVersionId })`.
  resolveSource: () => AiFieldGenerationSource | null;
  // Applies a generated value for NAME/DESCRIPTION/CODE (or a picked candidate
  // for CODE). The caller decides how: a form writes it via react-hook-form's
  // setValue; a read-only surface persists it via a mutation.
  onApplyValue?: (value: string) => void;
  // Applies a generated TAGS list. The caller resolves/creates each name to a
  // tag id however its surface requires.
  onApplyTagNames?: (names: string[]) => void;
  // Applies a generated CATEGORY id. The caller resolves/persists it however
  // its surface requires.
  onApplyCategoryId?: (categoryId: string) => void;
  // Remaining AI credits for the popover footer. Null/undefined → the line is
  // hidden (no cap, or AI budget query not loaded).
  creditsRemaining?: AiCredits | null;
  // Persisted detection state for this field (from `aiDetectionStatus` +
  // `suggestedCodes`, computed by the caller via `getAiFieldDetectionState`).
  // Undefined in create mode or when the doc has no detection history — the
  // trigger then behaves exactly as a plain idle/generated assist would.
  // A live, user-triggered generation in this session always takes priority.
  detectionUiState?: AiFieldUiState;
  // CODE field only: the persisted multi-candidate list backing a persisted
  // MultipleCodes state (mirrors the live `pendingCodes` picker).
  suggestedCodes?: string[];
  // Copy for the persisted Detecting row. (The Failed row always reads
  // "Detection failed" — a live error and a persisted failure look identical.)
  detectingLabel?: string;
  // Settle a persisted Failed/MultipleCodes state (Cancel) via the BE dismiss
  // mutation so it stops reappearing once resolved.
  onDismiss?: () => void;
  // CODE only, for read-only surfaces (View/List) rather than the form: in the
  // MultipleCodes popover, disables auto-apply-on-select — picking a candidate
  // only updates the local selection, and a Save button persists it explicitly.
  // Off by default — the form's multi-candidate picker keeps auto-applying on
  // select.
  showManualCodeInput?: boolean;
  // CODE + showManualCodeInput only: a Save of the currently radio-selected
  // candidate (MultipleCodes) is being persisted — shows a spinner on the Save
  // button.
  isSavingManualCode?: boolean;
  // Returns the target field's current value (scalar text, or the selected
  // category's id for CATEGORY). Snapshotted the moment a generation starts
  // and re-read on completion: if it no longer matches, the user edited the
  // field in the meantime — typed over it, or picked something else during a
  // batch autofill run (which can take up to ~90s) — so the generated value
  // is dropped instead of silently overwriting their edit. Omit where
  // overwrite isn't a concern (TAGS only appends; CODE's multi-candidate list
  // is picker-only, nothing is written until a candidate is chosen).
  getCurrentValue?: () => string;
};

// Single reusable AI auto-fill affordance for a documentation field: a 32x32
// IconButton at the field's own edge, opening a Popover with the
// generate/regenerate/detecting/failed/multiple-codes content on hover.
// Form-agnostic — applying a result is entirely the caller's job via
// onApplyValue/onApplyTagNames/onApplyCategoryId, so the exact same component
// renders on the create/edit form (via AiFormFieldAssist), the read-only
// document view, and the documents list.
export default function AiFieldAssist({
  field,
  labels,
  resolveSource,
  onApplyValue,
  onApplyTagNames,
  onApplyCategoryId,
  creditsRemaining,
  detectionUiState,
  suggestedCodes,
  detectingLabel,
  onDismiss,
  showManualCodeInput,
  isSavingManualCode,
  getCurrentValue,
}: Props) {
  const { t } = useTranslation();
  // Why AI spend is refused right now, if it is (BE guide §10). Null outside
  // the dashboard's AiBudgetProvider — a surface that cannot know the budget
  // blocks nothing and lets the mutation's own error code answer instead.
  const aiBudget = useSharedAiBudget();
  const aiBlockedText = getAiBudgetBlockedText(t, aiBudget?.blockedReason);
  const ownGeneration = useAiFieldGeneration();
  const sharedGeneration = useSharedAiFieldGeneration();
  const { getFieldState, generate, cancel, reset } =
    sharedGeneration ?? ownGeneration;
  const state = getFieldState(field);

  const isTagsField = field === AiDocumentationFieldEnum.Tags;
  const isCodeField = field === AiDocumentationFieldEnum.Code;
  const isCategoryField = field === AiDocumentationFieldEnum.Category;
  const showManualCode = isCodeField && showManualCodeInput;

  // When CODE generation returns more than one candidate, hold them here and
  // let the user pick, re-pickable until Dismiss. In the form
  // (showManualCodeInput off), a pick auto-applies immediately. In View/List
  // (showManualCodeInput on), a pick only updates this local selection —
  // nothing is persisted until Save is clicked (see showManualCode below).
  const [pendingCodes, setPendingCodes] = useState<string[] | null>(null);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  // Guards so the done/notFound side effects run once per generation. A live
  // error needs no side effect — it stays as the `error` status and renders
  // the inline Failed popover below (same UI as a BE-persisted failure).
  const appliedGenerationRef = useRef<string | null>(null);
  const notFoundGenerationRef = useRef<string | null>(null);

  // Snapshot the target field's value the instant a generation starts (not
  // just when this trigger's own runGeneration fires — a batch autofill
  // starts generations for every field at once from outside this component),
  // so completion can tell the field apart from one the user has since
  // edited. See getCurrentValue's doc comment for why this matters.
  const snapshotGenerationIdRef = useRef<string | null>(null);
  const valueAtGenerationStartRef = useRef('');
  useEffect(() => {
    if (
      state.status !== 'generating' ||
      !state.generationId ||
      snapshotGenerationIdRef.current === state.generationId
    ) {
      return;
    }
    snapshotGenerationIdRef.current = state.generationId;
    valueAtGenerationStartRef.current = getCurrentValue?.() ?? '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status, state.generationId]);

  const runGeneration = () => {
    // The CTA is already disabled while the budget refuses spend; this covers
    // the budget running out with the popover open, where the click would
    // otherwise start a generation the server rejects.
    if (aiBlockedText) return;
    const source = resolveSource();
    if (!source) return;
    generate({ field, source });
  };

  // Apply the generated value into the target surface, or surface a generic
  // error and fall back to the idle trigger, once per generation.
  useEffect(() => {
    if (!state.generationId) return;
    if (
      state.status === 'done' &&
      appliedGenerationRef.current !== state.generationId
    ) {
      appliedGenerationRef.current = state.generationId;
      // The user edited the field after this generation started — keep their
      // edit and drop the now-stale suggestion rather than overwrite it.
      const editedSinceGenerationStart =
        !!getCurrentValue &&
        getCurrentValue() !== valueAtGenerationStartRef.current;
      if (isTagsField) {
        // TAGS returns a list of suggested names in `values`.
        onApplyTagNames?.(state.values ?? []);
      } else if (isCodeField && (state.values?.length ?? 0) > 1) {
        // CODE detected several candidates — surface the picker so the user
        // chooses the correct code rather than silently taking the top match.
        setPendingCodes(state.values ?? []);
        setSelectedCode(null);
      } else if (isCategoryField) {
        // CATEGORY carries a bare id in `categoryId`.
        if (state.categoryId && !editedSinceGenerationStart) {
          onApplyCategoryId?.(state.categoryId);
        }
      } else {
        // Scalar fields (NAME/DESCRIPTION/NOTES) carry the value in `text`; a
        // single CODE candidate arrives in `values` (most-identifying first) —
        // apply it. Only write a non-empty value so an empty result (model
        // found nothing) never clears a value the user already typed (BE
        // guide §7: `text` may be empty, `values` may be an empty list).
        const generatedValue = state.text || state.values?.[0] || '';
        if (generatedValue && !editedSinceGenerationStart) {
          onApplyValue?.(generatedValue);
        }
      }
    }
    if (
      state.status === 'notFound' &&
      notFoundGenerationRef.current !== state.generationId
    ) {
      notFoundGenerationRef.current = state.generationId;
      // The model ran but had nothing to fill — surfaced inline below as the
      // neutral "no suggestions" state (Dismiss only; Regenerate won't help
      // unless the file changes).
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status, state.generationId]);

  // The "Completed" trigger is a transient success confirmation, not a
  // persistent indicator — after 5s it quiets back down to the idle trigger.
  useEffect(() => {
    if (state.status !== 'done') return undefined;
    const timer = setTimeout(() => reset(field), 5000);
    return () => clearTimeout(timer);
  }, [state.status, state.generationId, field, reset]);

  // Write a picked code immediately — no separate Apply step. The candidate
  // list is NOT cleared, so the popover can be reopened and a different
  // candidate picked again, repeatable until Dismiss.
  const applyCode = (code: string) => {
    setSelectedCode(code);
    onApplyValue?.(code);
  };

  // Settle the picker entirely: a persisted pick needs the BE dismiss mutation
  // so it stops reappearing; a live pick is a purely local reset.
  const dismissCodeSelection = (isPersistedPick: boolean) => {
    if (isPersistedPick) {
      onDismiss?.();
    } else {
      setPendingCodes(null);
      setSelectedCode(null);
      reset(field);
    }
  };

  const isGenerating = state.status === 'generating';
  const isGenerated = state.status === 'done';
  // A live generation in this session always takes priority over the
  // persisted detection state, mirroring getAiFieldDetectionState's own
  // priority order on the view page.
  const isLiveActive = state.status !== 'idle';
  const persistedUiState = detectionUiState ?? AiFieldUiState.None;
  const isDetectingPersisted =
    !isLiveActive && persistedUiState === AiFieldUiState.Detecting;
  const isFailedPersisted =
    !isLiveActive && persistedUiState === AiFieldUiState.Failed;
  // A live generation failure renders the exact same Failed popover as a
  // BE-persisted failure (red trigger + Dismiss/Retry), instead of a transient
  // toast — the reason is shown inline and the field keeps a persistent error
  // affordance. Cancel here is a purely local reset (no BE dismiss mutation).
  const isFailedLive = state.status === 'error';
  const isFailed = isFailedLive || isFailedPersisted;
  const isMultipleCodesPersisted =
    !isLiveActive &&
    isCodeField &&
    persistedUiState === AiFieldUiState.MultipleCodes;

  // Multi-candidate CODE result: a freshly generated list (pendingCodes)
  // takes priority over the persisted `suggestedCodes`; either way the user
  // picks (and re-picks) before Dismiss settles it.
  const codesToPick =
    pendingCodes ?? (isMultipleCodesPersisted ? (suggestedCodes ?? []) : null);

  // ── Multiple candidates: radio list, re-pickable until Dismiss ──
  // Form usage (showManualCode off) auto-applies on select, as before. View/List
  // usage (showManualCode on) only updates the local selection on pick — nothing
  // is persisted until Save is clicked.
  if (codesToPick && codesToPick.length > 0) {
    const isPersistedPick = !pendingCodes;
    const selectCode = (code: string) => {
      if (showManualCode) {
        setSelectedCode(code);
      } else {
        applyCode(code);
      }
    };
    const codePickerBody = () => (
      <CodePickerContent
        codes={codesToPick}
        selectedCode={selectedCode}
        showManualCode={!!showManualCode}
        isSavingManualCode={isSavingManualCode}
        onSelect={selectCode}
        onCancel={() => dismissCodeSelection(isPersistedPick)}
        onApply={() => selectedCode && onApplyValue?.(selectedCode)}
        creditsRemaining={creditsRemaining}
      />
    );
    return (
      <AiFieldAssistPopover
        icon="help"
        symbolsOutlined
        iconButtonType={IconButtonTypes.WarningOutlined}
        ariaLabel={t('ai-review-and-edit')}
        body={codePickerBody}
      />
    );
  }

  // ── BE-persisted "Detecting" (QUEUED/DETECTING) ──
  if (isDetectingPersisted) {
    const label = detectingLabel ?? labels.idleCta;
    const detectingBody = () => (
      <DetectingContent label={label} creditsRemaining={creditsRemaining} />
    );
    return (
      <AiFieldAssistPopover
        isLoading
        iconButtonType={IconButtonTypes.PrimaryOutlined}
        ariaLabel={label}
        body={detectingBody}
      />
    );
  }

  // ── Failed / No suggestions found — same red affordance, different copy +
  // actions ──
  // Failed (live error or BE-persisted): "Detection failed" + Dismiss & Retry.
  // No suggestions (live only: the model ran but had nothing to fill): "No
  // suggestions found" + Dismiss only — Regenerate won't help unless the file
  // changes, so no Retry (and no manual-code row).
  const isNotFoundLive = state.status === 'notFound';
  if (isFailed || isNotFoundLive) {
    // NAME/DESCRIPTION/NOTES are free-text the model writes ("Generation
    // failed"); CODE/TAGS/CATEGORY are detected/picked from the file or
    // existing entities ("Detection failed").
    const isGenerationField =
      field === AiDocumentationFieldEnum.Name ||
      field === AiDocumentationFieldEnum.Description ||
      field === AiDocumentationFieldEnum.Notes;
    const failedLabel = isGenerationField
      ? t('ai-generation-failed')
      : t('ai-detection-failed');
    const label = isNotFoundLive ? t('ai-no-suggestion-found') : failedLabel;
    // Cancel must fully settle the field. Clear any live result (error /
    // no-suggestions), AND — when a BE-persisted state is still underneath it
    // (e.g. the user retried a persisted failure and the retry found nothing) —
    // dismiss that too via the mutation, or it would re-surface the instant the
    // live state is cleared.
    const dismiss = () => {
      if (persistedUiState !== AiFieldUiState.None) onDismiss?.();
      if (isLiveActive) reset(field);
    };
    const errorBody = () => (
      <ErrorContent
        label={label}
        showRetry={!isNotFoundLive}
        onCancel={dismiss}
        onRetry={runGeneration}
        creditsRemaining={creditsRemaining}
        blockedText={aiBlockedText}
      />
    );
    return (
      <AiFieldAssistPopover
        icon="error"
        symbolsOutlined
        iconButtonType={IconButtonTypes.NegativeOutlined}
        ariaLabel={label}
        body={errorBody}
      />
    );
  }

  // ── Idle / live generating / generated ──
  const idleOrGeneratedLabel = isGenerated
    ? labels.regenerateCta
    : labels.idleCta;
  const mainBody = () => (
    <MainContent
      isGenerating={isGenerating}
      isGenerated={isGenerated}
      idleLabel={labels.idleLabel}
      ctaLabel={idleOrGeneratedLabel}
      detectingLabel={detectingLabel ?? labels.idleCta}
      onGenerate={runGeneration}
      onCancel={() => cancel(field)}
      creditsRemaining={creditsRemaining}
      blockedText={aiBlockedText}
    />
  );
  return (
    <AiFieldAssistPopover
      icon={isGenerated ? 'check_circle' : 'auto_awesome'}
      symbolsOutlined
      isLoading={isGenerating}
      iconButtonType={IconButtonTypes.PrimaryOutlined}
      ariaLabel={idleOrGeneratedLabel}
      body={mainBody}
    />
  );
}
